import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Legend,
} from 'recharts';
import api from '../../api/api';

const number = value => value == null ? '—' : Number(value).toLocaleString('en-GB');
const monthLabel = value => {
  if (!value) return '';
  const parsed = new Date(`${value}-01T12:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'Europe/London' });
};
const dateTime = value => {
  if (!value) return 'Never';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Unknown' : parsed.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
};
const reportDay = value => value ? new Date(`${value}T12:00:00Z`).toLocaleDateString('en-GB', { dateStyle: 'medium', timeZone: 'Europe/London' }) : 'None yet';
const shortLabel = value => String(value || '').length > 22 ? `${String(value).slice(0, 21)}…` : String(value || '');
const asArray = value => Array.isArray(value) ? value : [];
const chartNumber = value => value == null ? null : Number(value);
const METRIC_COLUMNS = [
  ['pages', 'Pages'], ['jobs', 'Jobs'], ['colour_pages', 'Colour'],
  ['mono_pages', 'B&W'], ['simplex_pages', 'Single-sided'], ['duplex_pages', 'Double-sided'],
];
const ACTIVITY_OPTIONS = [
  ['colour_pages', 'Colour printing'], ['mono_pages', 'Black & white printing'],
  ['simplex_pages', 'Single-sided printing'], ['duplex_pages', 'Double-sided printing'],
];
const IMPORT_ERRORS = {
  CSV_VALIDATION_FAILED: 'The report could not be validated.',
  DOMAIN_MAPPING_FAILED: 'Check the active school domain mappings.',
  REPORT_DATE_INVALID: 'The reporting date could not be validated.',
  REPORT_DAY_CONFLICT: 'A different report has already been imported for this day. Existing totals were retained.',
  DUPLICATE_DAY: 'This reporting day has already been imported. It was not added again.',
  ATTACHMENT_OR_IMPORT_FAILED: 'The report attachment could not be imported.',
  IMPORT_FAILED: 'The report could not be imported.',
};

function ChartTooltip({ active, payload, label, metricLabel }) {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  const row = item.payload || {};
  return (
    <div className="pd-tooltip">
      <strong>{row.tooltipLabel || row.name || label}</strong>
      <span>{number(item.value)} {metricLabel.toLowerCase()}</span>
      {row.school && <small>{row.school}</small>}
    </div>
  );
}

function UsageChart({ title, caption, rows, colour, horizontal = false, onSelect, disabled = false, controls = false, selectedKey = '', pie = false }) {
  const data = rows.filter(row => row.value != null && Number.isFinite(Number(row.value)));
  const renderInteractiveBar = shape => {
    const row = data[shape.index] || shape.payload;
    const activate = event => {
      event.preventDefault(); event.stopPropagation();
      if (!disabled && row) onSelect(row);
    };
    const x = Number(shape.x) || 0, y = Number(shape.y) || 0;
    const width = Math.max(0, Number(shape.width) || 0), height = Math.max(0, Number(shape.height) || 0);
    return (
      <g role="button" tabIndex={disabled ? -1 : 0} aria-disabled={disabled}
        aria-label={`Filter by ${row ? row.name : 'activity'}`} aria-pressed={Boolean(row && selectedKey && row.key === selectedKey)}
        data-pd-bar={row ? row.name : ''} onClick={activate}
        onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') activate(event); }}>
        <rect x={x} y={y} width={width} height={height} rx={4} fill={shape.fill || colour} />
        <rect x={x} y={height < 14 && !horizontal ? y - (14 - height) : y}
          width={Math.max(width, 14)} height={Math.max(height, 14)} fill="transparent" pointerEvents="all" />
      </g>
    );
  };
  return (
    <section className={`pd-panel pd-chart-panel${horizontal ? ' pd-chart-wide' : ''}`} aria-busy={disabled}>
      <div className="pd-panel-heading">
        <h2>{title}</h2>
        {caption && <p>{caption}</p>}
      </div>
      {(!data.length || (pie && !data.some(row => Number(row.value) > 0))) ? <div className="pd-chart-empty">No report data available for this selection.</div> : (
        <div className={`pd-chart${horizontal ? ' pd-chart-horizontal' : ''}`} aria-label={title}>
          <ResponsiveContainer width="100%" height="100%">
            {pie ? <PieChart>
              <Tooltip content={<ChartTooltip metricLabel="Pages" />} />
              <Legend />
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius="75%"
                isAnimationActive={false} cursor={disabled ? 'wait' : onSelect ? 'pointer' : 'default'}
                onClick={row => { if (!disabled && onSelect) onSelect(row); }}>
                {data.map((row, index) => <Cell key={row.key || index} fill={row.colour || colour} />)}
              </Pie>
            </PieChart> : <BarChart data={data} layout={horizontal ? 'vertical' : 'horizontal'} accessibilityLayer
              margin={{ top: 12, right: 24, left: horizontal ? 8 : 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--nj-border)" horizontal={!horizontal} vertical={horizontal} />
              {horizontal ? <>
                <XAxis type="number" allowDecimals={false} tickFormatter={number} stroke="var(--nj-border)" />
                <YAxis type="category" dataKey="name" width={180} tickFormatter={shortLabel} interval={0} stroke="var(--nj-border)" tickLine={false} />
              </> : <>
                <XAxis dataKey="name" tickFormatter={shortLabel} interval="preserveStartEnd" minTickGap={12} height={48} stroke="var(--nj-border)" tickLine={false} />
                <YAxis allowDecimals={false} tickFormatter={number} stroke="var(--nj-border)" width={64} />
              </>}
              <Tooltip cursor={{ fill: 'var(--pd-chart-hover)' }} content={<ChartTooltip metricLabel="Pages" />} />
              <Bar dataKey="value" name="Pages" fill={colour} maxBarSize={54} radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                cursor={disabled ? 'wait' : onSelect ? 'pointer' : 'default'} shape={onSelect ? renderInteractiveBar : undefined} isAnimationActive={false}>
                {data.map((row, index) => <Cell key={row.key || index} fill={row.colour || colour} />)}
              </Bar>
            </BarChart>}
          </ResponsiveContainer>
        </div>
      )}
      {controls && onSelect && rows.length > 0 && <div className="pd-panel-heading pd-heading-row" role="group" aria-label={`${title} filters`}>
        {rows.map(row => <button key={row.key} type="button" disabled={disabled} aria-pressed={selectedKey === row.key}
          className={`pd-button${selectedKey === row.key ? ' pd-button-primary' : ''}`} onClick={() => onSelect(row)}>{row.name}</button>)}
      </div>}
    </section>
  );
}

export default function PrintDashboard() {
  const [period, setPeriod] = useState('all');
  const [monthFrom, setMonthFrom] = useState('');
  const [monthTo, setMonthTo] = useState('');
  const [school, setSchool] = useState('');
  const [activity, setActivity] = useState('');
  const [canSeeUnmatched, setCanSeeUnmatched] = useState(false);
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('pages');
  const [direction, setDirection] = useState('desc');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [schools, setSchools] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const userSection = useRef(null);
  const pendingUserScroll = useRef(false);
  const [displayedFilters, setDisplayedFilters] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(input.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [input]);

  const invalidRange = Boolean(monthFrom && monthTo && monthFrom > monthTo);
  const scopeKey = JSON.stringify([school, monthFrom, monthTo, activity]);
  const query = useMemo(() => new URLSearchParams({
    ...(school ? { school_id: school } : {}),
    ...(monthFrom ? { month_from: monthFrom } : {}),
    ...(monthTo ? { month_to: monthTo } : {}),
    ...(activity ? { activity } : {}),
    search, sort, direction, page: String(page),
  }).toString(), [school, monthFrom, monthTo, activity, search, sort, direction, page]);

  useEffect(() => {
    let active = true;
    // Keep the entire dashboard mounted during every request, including chart filters.
    // Labels stay tied to the displayed result until its replacement is ready.
    setError('');
    if (invalidRange) { setBusy(false); return () => { active = false; }; }
    setBusy(true);
    api(`/printing?${query}`)
      .then(result => {
        if (active) { setData(result); setDisplayedFilters({ key: scopeKey, values: JSON.parse(scopeKey) }); setSchools(asArray(result.schools)); setCanSeeUnmatched(Boolean(result.global)); }
      })
      .catch(err => { if (active) { setData(null); setError(err.message || 'Unable to load printing analytics.'); setSchools([]); setCanSeeUnmatched(false); } })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [query, refresh, invalidRange, scopeKey]);

  useEffect(() => {
    const timer = setInterval(() => setRefresh(value => value + 1), 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!busy && data && pendingUserScroll.current) {
      pendingUserScroll.current = false;
      if (userSection.current) userSection.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [busy, data]);

  function choosePeriod(value) {
    setPeriod(value); setPage(1);
    if (value === 'all') { setMonthFrom(''); setMonthTo(''); return; }
    if (value === 'custom') return;
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
    const year = Number(parts.find(part => part.type === 'year').value);
    const month = Number(parts.find(part => part.type === 'month').value);
    setMonthTo(`${year}-${String(month).padStart(2, '0')}`);
    setMonthFrom(new Date(Date.UTC(year, month - Number(value), 1)).toISOString().slice(0, 7));
  }
  function selectSchool(id) { setSchool(String(id)); setPage(1); }
  function selectActivity(key) {
    setActivity(current => current === key ? '' : key); setPage(1);
    setSort(key); setDirection('desc'); pendingUserScroll.current = false;
  }
  function selectMonth(row) {
    if (!row || !row.month) return;
    if (monthFrom === row.month && monthTo === row.month) { choosePeriod('all'); return; }
    setPeriod('custom'); setMonthFrom(row.month); setMonthTo(row.month); setPage(1);
  }
  function selectUser(row) {
    if (!row || !row.email) return;
    const value = input === row.email ? '' : row.email;
    setInput(value); setSearch(value); setPage(1); pendingUserScroll.current = true;
  }
  function clearFilters() {
    setSchool(''); setActivity(''); setPeriod('all'); setMonthFrom(''); setMonthTo(''); setInput(''); setSearch(''); setPage(1);
  }
  function changeSort(key) {
    pendingUserScroll.current = false;
    setDirection(sort === key && direction === 'desc' ? 'asc' : 'desc'); setSort(key); setPage(1);
  }

  const totals = data && data.totals ? data.totals : {};
  const snapshot = data && data.snapshot;
  const available = snapshot ? asArray(snapshot.available) : [];
  const has = key => available.includes(key);
  const singleSchool = schools.length === 1;
  const bySchool = asArray(data && data.bySchool);
  const byMonth = asArray(data && data.byMonth);
  const topUsers = asArray(data && data.topUsers);
  const users = asArray(data && data.users);
  const latestAttempt = data && data.latestAttempt;
  const stale = snapshot && Date.now() - new Date(snapshot.received_at).getTime() > 36 * 60 * 60 * 1000;
  const failed = latestAttempt && latestAttempt.status === 'failed';
  const statusText = failed ? 'Import needs attention' : !snapshot ? 'Awaiting first import' : stale ? 'Update overdue' : 'Monthly data available';
  const hasFilters = Boolean(school || activity || period !== 'all' || input);
  const [shownSchool, shownMonthFrom, shownMonthTo, shownActivity] = displayedFilters ? displayedFilters.values : [school, monthFrom, monthTo, activity];
  const periodText = shownMonthFrom || shownMonthTo ? `${shownMonthFrom ? monthLabel(shownMonthFrom) : 'Earliest month'} – ${shownMonthTo ? monthLabel(shownMonthTo) : 'Latest month'}` : 'All stored months';
  const scopeChanging = Boolean(data && displayedFilters && displayedFilters.key !== scopeKey);
  const activityName = (ACTIVITY_OPTIONS.find(([key]) => key === shownActivity) || [])[1];
  const schoolName = shownSchool === 'unmatched' ? 'Unmatched domains' : shownSchool ? (schools.find(item => String(item.id) === shownSchool) || {}).name : 'All permitted schools';
  const metric = key => has(key) ? number(totals[key]) : '—';
  const share = key => has(key) && Number(totals.pages) > 0 ? `${(Number(totals[key]) / Number(totals.pages) * 100).toLocaleString('en-GB', { maximumFractionDigits: 1 })}% of pages` : 'Pages in the selected period';
  const pageCount = Math.max(1, Math.ceil(Number(data && data.count || 0) / Number(data && data.pageSize || 50)));
  const summaryCards = [
    { label: 'Total pages', value: number(totals.pages || 0), detail: periodText },
    { label: 'Print & copy jobs', value: metric('jobs'), detail: 'Reported jobs' },
    { label: 'Unique users', value: number(totals.users || 0), detail: 'Across the selected schools' },
    { label: 'Schools with activity', value: number(bySchool.filter(item => item.school_id != null).length), detail: 'In the selected period' },
  ];
  const breakdowns = [
    { key: 'colour_pages', label: 'Colour pages', colour: 'purple' },
    { key: 'mono_pages', label: 'Black & white pages', colour: 'blue' },
    { key: 'simplex_pages', label: 'Single-sided pages', colour: 'pink' },
    { key: 'duplex_pages', label: 'Double-sided pages', colour: 'cyan' },
  ];

  return (
    <div className="print-dashboard">
      <style>{`
        @media (min-width: 1100px) {
          .print-dashboard .pd-chart-grid.pd-single-school {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
          .print-dashboard .pd-chart-grid.pd-single-school > .pd-chart-wide {
            grid-column: 1 / -1;
          }
        }
      `}</style>
      <header className="pd-heading">
        <div>
          <h1>Printing Analytics</h1>
          <p>School and trust printing activity, summarised by month.</p>
          {snapshot && <p className="pd-heading-updated">Last updated: {dateTime(snapshot.updated_at)}</p>}
        </div>
      </header>

      <section className="pd-panel pd-filter-panel" aria-label="Printing filters">
        <div className="pd-panel-heading pd-heading-row">
          <h2>Filters</h2>
          {hasFilters && <button type="button" className="pd-text-button" onClick={clearFilters}>Clear filters</button>}
        </div>
        <div className="pd-filter-grid">
          <label htmlFor="printing-school">School
            <select id="printing-school" value={school} onChange={event => selectSchool(event.target.value)}>
              <option value="">All permitted schools</option>
              {schools.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
              {canSeeUnmatched && <option value="unmatched">Unmatched domains</option>}
            </select>
          </label>
          <label htmlFor="printing-period">Period
            <select id="printing-period" value={period} onChange={event => choosePeriod(event.target.value)}>
              <option value="all">All stored months</option><option value="1">This month</option><option value="3">Last 3 months</option>
              <option value="6">Last 6 months</option><option value="12">Last 12 months</option><option value="custom">Custom month range</option>
            </select>
          </label>
          <label htmlFor="printing-activity">User activity
            <select id="printing-activity" value={activity} onChange={event => { setActivity(event.target.value); setPage(1); if (event.target.value) { setSort(event.target.value); setDirection('desc'); } }}>
              <option value="">All users</option>
              {ACTIVITY_OPTIONS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </label>
          {period === 'custom' && <>
            <label htmlFor="printing-month-from">From month<input id="printing-month-from" type="month" value={monthFrom} max={monthTo || undefined} onChange={event => { setMonthFrom(event.target.value); setPage(1); }} /></label>
            <label htmlFor="printing-month-to">To month<input id="printing-month-to" type="month" value={monthTo} min={monthFrom || undefined} onChange={event => { setMonthTo(event.target.value); setPage(1); }} /></label>
          </>}
        </div>
        <p className="pd-filter-note">School and period apply to all summaries and charts. Activity selects users with that printing mode; totals include all page types for those users.</p>
        {data && scopeChanging && !invalidRange && <p className="pd-filter-note" role="status">Updating filters. Previous results remain visible until the new selection is ready.</p>}
        {invalidRange && <p className="pd-error-inline" role="alert">Choose an end month on or after the start month.</p>}
      </section>

      {error && <div className="pd-error" role="alert">{error}<button type="button" className="pd-text-button" onClick={() => setRefresh(value => value + 1)}>Try again</button></div>}
      {busy && !data && <div className="pd-loading" role="status">Loading printing analytics…</div>}

      {data && <>
        <div className="pd-context-row">
          <p><strong>{schoolName || 'Selected school'}</strong> · {periodText}{activityName ? ` · Users with ${activityName.toLowerCase()}` : ''}</p>
          <span className={`pd-status-badge ${failed ? 'pd-status-error' : stale || !snapshot ? 'pd-status-warning' : 'pd-status-success'}`}>{busy || scopeChanging ? 'Updating results…' : statusText}</span>
        </div>
        {!snapshot ? <section className="pd-panel pd-empty-state"><h2>Awaiting the first successful import</h2><p>Printing analytics will appear after a daily report has been imported.</p></section> : <>
          <div className="pd-summary-grid">
            {summaryCards.map(card => <section className="pd-panel pd-summary-card" key={card.label}><h2>{card.label}</h2><strong>{card.value}</strong><p>{card.detail}</p></section>)}
          </div>
          <div className="pd-breakdown-grid">
            {breakdowns.map(item => <section className={`pd-panel pd-breakdown-card pd-accent-${item.colour}`} key={item.key}><h2>{item.label}</h2><strong>{metric(item.key)}</strong><p>{share(item.key)}</p></section>)}
          </div>

          <div className={`pd-chart-grid${singleSchool ? ' pd-single-school' : ''}`}>
            {!singleSchool && <UsageChart disabled={busy || scopeChanging || invalidRange} title="Pages by school" caption="Select a school bar to filter the dashboard."
              rows={bySchool.map(item => ({ ...item, key: item.school_id || 'unmatched', value: chartNumber(item.pages) }))}
              colour="var(--pd-chart-green)" onSelect={row => { if (row) { const key = row.school_id == null ? 'unmatched' : String(row.school_id); selectSchool(school === key ? '' : key); } }} />}
            <UsageChart disabled={busy || scopeChanging || invalidRange} title="Pages by month" caption="Select a month to focus on it; select it again to show all months."
              rows={byMonth.map(item => ({ ...item, key: item.month, name: monthLabel(item.month), value: chartNumber(item.pages) }))}
              colour="var(--pd-chart-blue)" onSelect={selectMonth} />
            <UsageChart disabled={busy || scopeChanging || invalidRange} title="Colour and black & white" pie controls selectedKey={activity} caption="Select a slice or the buttons below to filter users; select again to clear."
              rows={[{ key: 'colour_pages', name: 'Colour', colour: 'var(--pd-chart-purple)' }, { key: 'mono_pages', name: 'Black & white', colour: 'var(--pd-chart-muted)' }].filter(item => has(item.key)).map(item => ({ ...item, value: chartNumber(totals[item.key]) }))}
              colour="var(--pd-chart-purple)" onSelect={row => { if (row) selectActivity(row.key); }} />
            <UsageChart disabled={busy || scopeChanging || invalidRange} title="Single-sided and double-sided" pie controls selectedKey={activity} caption="Select a slice or the buttons below to filter users; select again to clear."
              rows={[{ key: 'simplex_pages', name: 'Single-sided', colour: 'var(--pd-chart-pink)' }, { key: 'duplex_pages', name: 'Double-sided', colour: 'var(--pd-chart-cyan)' }].filter(item => has(item.key)).map(item => ({ ...item, value: chartNumber(totals[item.key]) }))}
              colour="var(--pd-chart-pink)" onSelect={row => { if (row) selectActivity(row.key); }} />
            <UsageChart disabled={busy || scopeChanging || invalidRange} title="Most pages by user" caption="Top 10 user/school totals. Select a bar to find that user in the breakdown."
              rows={topUsers.map(item => ({ ...item, key: `${item.email}|${item.school_id}`, name: item.name || item.email, tooltipLabel: item.name ? `${item.name} · ${item.email}` : item.email, value: chartNumber(item.pages) }))}
              colour="var(--pd-chart-cyan)" horizontal onSelect={selectUser} />
          </div>

          <section className="pd-panel pd-table-panel">
            <div className="pd-panel-heading"><h2>School summary</h2><p>Totals for {periodText.toLowerCase()}. Select a school name to focus on it.</p></div>
            <div className="pd-table-wrap"><table className="pd-table"><thead><tr><th scope="col">School</th><th scope="col" className="pd-number">Pages</th><th scope="col" className="pd-number">Jobs</th><th scope="col" className="pd-number">Users</th><th scope="col" className="pd-number">Colour</th><th scope="col" className="pd-number">Black & white</th></tr></thead>
              <tbody>{bySchool.map(item => <tr key={item.school_id || 'unmatched'}><th scope="row">{item.school_id != null || canSeeUnmatched ? <button type="button" className="pd-text-button" disabled={busy || scopeChanging || invalidRange} onClick={() => { const key = item.school_id == null ? 'unmatched' : String(item.school_id); selectSchool(school === key ? '' : key); }}>{item.name}</button> : item.name}</th><td className="pd-number">{number(item.pages)}</td><td className="pd-number">{number(item.jobs)}</td><td className="pd-number">{number(item.users)}</td><td className="pd-number">{number(item.colour_pages)}</td><td className="pd-number">{number(item.mono_pages)}</td></tr>)}</tbody>
            </table></div>
            {!bySchool.length && <p className="pd-table-empty">No school activity was imported for this selection.</p>}
          </section>

          <section className="pd-panel pd-table-panel" ref={userSection} aria-busy={busy}>
            <div className="pd-panel-heading"><h2>User breakdown</h2><p>{number(data.count)} matching user/school totals. Select a column heading to sort. Unreported figures appear as —. {busy && <span role="status">Updating results…</span>}</p></div>
            <div className="pd-filter-grid" style={{ padding: '0 20px 20px' }}>
          <label htmlFor="printing-user-search">Find user
            <input id="printing-user-search" value={input} onChange={event => setInput(event.target.value)} placeholder="Name or email address" maxLength={200} />
          </label>
            </div>
            <div className="pd-table-wrap"><table className="pd-table"><thead><tr>
              {[['name', 'Name'], ['email', 'Email'], ['school', 'School'], ...METRIC_COLUMNS].map(([key, label]) => <th key={key} scope="col" className={['name', 'email', 'school'].includes(key) ? '' : 'pd-number'} aria-sort={sort === key ? direction === 'asc' ? 'ascending' : 'descending' : 'none'}><button type="button" className="pd-sort-button" disabled={scopeChanging || invalidRange} onClick={() => changeSort(key)}>{label}<span aria-hidden="true">{sort === key ? direction === 'asc' ? ' ↑' : ' ↓' : ''}</span></button></th>)}
            </tr></thead><tbody>{users.map(item => <tr key={`${item.email}|${item.school_id == null ? 'unmatched' : item.school_id}`}><td>{item.name || '—'}</td><td>{item.email}</td><td>{item.school || 'Unmatched'}</td>{METRIC_COLUMNS.map(([key]) => <td className="pd-number" key={key}>{number(item[key])}</td>)}</tr>)}</tbody></table></div>
            {!users.length && <p className="pd-table-empty">No users match the current filters.</p>}
            <div className="pd-pagination"><span>Page {page} of {pageCount}</span><button type="button" className="pd-button" disabled={page <= 1 || scopeChanging || invalidRange} onClick={() => setPage(value => value - 1)}>Previous</button><button type="button" className="pd-button" disabled={page >= pageCount || scopeChanging || invalidRange} onClick={() => setPage(value => value + 1)}>Next</button></div>
          </section>
        </>}

        <details className="pd-panel pd-import-details">
          <summary>Import details</summary>
          <dl><div><dt>Last updated</dt><dd>{dateTime(snapshot && snapshot.updated_at)}</dd></div><div><dt>Last report received</dt><dd>{dateTime(snapshot && snapshot.received_at)}</dd></div><div><dt>Latest reporting day</dt><dd>{reportDay(snapshot && snapshot.latest_report_date)}</dd></div><div><dt>Daily reports in selected period</dt><dd>{number(data.importedDays)}</dd></div></dl>
          {stale && <p>The last successful report was received over 36 hours ago. Stored monthly totals remain available.</p>}
          {latestAttempt && <p>Latest attempt: {latestAttempt.error_code === 'DUPLICATE_DAY' ? 'Skipped duplicate' : latestAttempt.status} · {dateTime(latestAttempt.attempted_at)}{latestAttempt.error_code ? ` · ${IMPORT_ERRORS[latestAttempt.error_code] || latestAttempt.error_code}` : ''}</p>}
          {latestAttempt && latestAttempt.unmatched_count > 0 && <div className="pd-unmatched"><h3>Unmatched domains</h3><p>{number(latestAttempt.unmatched_count)} report users could not be matched to an active school domain.</p><ul>{Object.entries(latestAttempt.unmatched_domains || {}).map(([domain, count]) => <li key={domain}>{domain}: {number(count)}</li>)}</ul></div>}
          <p>Totals include successfully imported days only. This dashboard refreshes automatically every five minutes; the mailbox is checked separately every five minutes.</p>
        </details>
      </>}
    </div>
  );
}
